import { FC } from 'react';
import {
    Row, Col,
    Typography,
    Card,
    List,
    Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
    AiOutlineGlobal,
    AiOutlineMail,
    AiOutlinePhone,
    AiOutlineUser,
} from 'react-icons/ai';
import {
    FaAddressCard,
    FaCarSide,
    FaMotorcycle,
    FaSprayCan,
} from 'react-icons/fa';
import { BsCalendarDate, BsTruck } from 'react-icons/bs';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';
import { IDriver } from '../../interfaces/driver.interface';

const { Title } = Typography;

const INFORMATION = 'INFORMATION';
const VEHICLE = 'VEHICLE';

const DriverInformation: FC<{ driver: IDriver }> = ({ driver }) => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const left = [
        {
            icon: <AiOutlineUser size={20} />,
            text: `${driver?.name} ${driver?.lastName}`,
        },
        {
            icon: <AiOutlineMail size={20} />,
            text: driver?.email,
        },
        {
            icon: <AiOutlinePhone size={20} />,
            text: driver?.fullMobile || `${driver?.countryCode} ${driver?.mobile}`,
        },
    ];

    const right = [
        {
            icon: <AiOutlineGlobal size={20} />,
            text: driver?.language === 'ES' ? t('SPANISH') : t('ENGLISH'),
        },
        {
            icon: <HiOutlineCurrencyDollar size={20} />,
            text: driver?.currency,
        },
    ];

    return (
        <Card>
            <Row justify="space-between" align="top" gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                    <List
                        itemLayout="horizontal"
                        dataSource={left}
                        renderItem={(item, index) => (
                            <List.Item key={`UA_${index}`}>
                                <List.Item.Meta
                                    avatar={item.icon}
                                    title={item.text}
                                />
                            </List.Item>
                        )}
                    />
                </Col>
                <Col xs={24} xl={12}>
                    <List
                        itemLayout="horizontal"
                        dataSource={right}
                        renderItem={(item, index) => (
                            <List.Item key={`UB_${index}`}>
                                <List.Item.Meta
                                    avatar={item.icon}
                                    title={item.text}
                                />
                            </List.Item>
                        )}
                    />
                </Col>
            </Row>
        </Card>
    );
};

const VehicleInformation: FC<{ driver: IDriver }> = ({ driver }) => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });
    const left = [
        {
            icon: (
                <Tooltip title={t(`${driver?.vehicleType}`)}>
                    {driver?.vehicleType === 'BIKE' ? (
                        <FaMotorcycle size={20} />
                    ) : (
                        <FaCarSide size={20} />
                    )}
                </Tooltip>
            ),
            text: driver?.vehicleModelName,
        },
        {
            icon: <FaAddressCard size={20} />,
            text: driver?.MCNumber,
        },
    ];

    const right = [
        {
            icon: <BsCalendarDate size={20} />,
            text: driver?.vehicleYear,
        },
        {
            icon: <FaSprayCan size={20} />,
            text: driver?.vehicleColor,
        },
    ];

    return (
        <Card>
            <Row justify="space-between" align="top" gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                    <List
                        itemLayout="horizontal"
                        dataSource={left}
                        renderItem={(item, index) => (
                            <List.Item key={`VA_${index}`}>
                                <List.Item.Meta
                                    avatar={item.icon}
                                    title={item.text}
                                />
                            </List.Item>
                        )}
                    />
                </Col>
                <Col xs={24} xl={12}>
                    <List
                        itemLayout="horizontal"
                        dataSource={right}
                        renderItem={(item, index) => (
                            <List.Item key={`VB_${index}`}>
                                <List.Item.Meta
                                    avatar={item.icon}
                                    title={item.text}
                                />
                            </List.Item>
                        )}
                    />
                </Col>
            </Row>
        </Card>
    );
};

const ContainerData: FC<{ name: string, keyName: string, driver: IDriver }> = ({ name, keyName, driver }) => (
    <div style={{ marginBottom: 24 }}>
        <Title level={5}>
            {name}
        </Title>
        {
            keyName === INFORMATION
                ? <DriverInformation driver={driver} />
                : <VehicleInformation driver={driver} />
        }
    </div>
);

const DriverCard: FC<{ driver: IDriver }> = ({ driver }) => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const CONTAINERS = {
        DRIVER: {
            key: 0,
            keyName: INFORMATION,
            icon: <AiOutlineUser color="#FFF" size={18} />,
            name: t('DRIVER_INFORMATION'),
        },
        VEHICLE: {
            key: 1,
            keyName: VEHICLE,
            icon: <BsTruck color="#FFF" size={18} />,
            name: t('VEHICLE_INFORMATION'),
        },
    };

    return (
        <Row justify="space-between" gutter={[24, 32]}>
            <Col xs={24} xxl={12}>
                <ContainerData
                    name={CONTAINERS.DRIVER.name}
                    keyName={CONTAINERS.DRIVER.keyName}
                    driver={driver}
                />
            </Col>
            <Col xs={24} xxl={12}>
                <ContainerData
                    name={CONTAINERS.VEHICLE.name}
                    keyName={CONTAINERS.VEHICLE.keyName}
                    driver={driver}
                />
            </Col>
        </Row>
    );
};

export default DriverCard;
